import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useLots } from '../../../lib/lots-api';
import { useProducts } from '../../../lib/products-api';

type CategoryStat = {
  category: string;
  lotCount: number;
  totalQty: number;
};

export function CategoryAggregateWidget() {
  const { t } = useTranslation();
  const lotsQ = useLots({ status: 'ACTIVE', pageSize: 200 });
  const productsQ = useProducts({ status: 'ACTIVE', pageSize: 200 });

  const stats = useMemo<CategoryStat[]>(() => {
    const products = productsQ.data?.items ?? [];
    const lots = lotsQ.data?.items ?? [];
    const productCategoryMap = new Map(products.map((p) => [p.id, p.category]));
    const map = new Map<string, CategoryStat>();
    for (const l of lots) {
      const cat = productCategoryMap.get(l.productId) ?? 'ALTRO';
      const cur = map.get(cat) ?? { category: cat, lotCount: 0, totalQty: 0 };
      cur.lotCount++;
      cur.totalQty += Number(l.residualQty);
      map.set(cat, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.totalQty - a.totalQty);
  }, [lotsQ.data, productsQ.data]);

  const isLoading = lotsQ.isLoading || productsQ.isLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.categoryAggregate.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {!isLoading && stats.length === 0 && (
          <p className="text-sm text-slate-500">{t('dashboard.categoryAggregate.empty')}</p>
        )}
        {stats.length > 0 && (
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-2 py-1 text-left font-medium text-slate-600">
                  {t('dashboard.categoryAggregate.col.category')}
                </th>
                <th className="px-2 py-1 text-right font-medium text-slate-600">
                  {t('dashboard.categoryAggregate.col.lotCount')}
                </th>
                <th className="px-2 py-1 text-right font-medium text-slate-600">
                  {t('dashboard.categoryAggregate.col.totalQty')}
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.category} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1 text-slate-700">{s.category}</td>
                  <td className="px-2 py-1 text-right font-mono">{s.lotCount}</td>
                  <td className="px-2 py-1 text-right font-mono">
                    {(s.totalQty / 1000).toFixed(1)} kg
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
